import { ReactNode } from "react";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import {
    LoaderOnInitialLoading,
    LoaderOnInitialLoadingQueryResult,
} from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";

interface RetryableQueryState {
    isError: boolean;
    refetch: () => void;
}

interface LedgersQueryState extends RetryableQueryState {
    isLoading: boolean;
}

interface ReportQueryResult<TData>
    extends LoaderOnInitialLoadingQueryResult<TData>, RetryableQueryState {}

interface Props<TData> {
    queryResult: ReportQueryResult<TData>;
    ledgerId: string | undefined;
    ledgersQuery: LedgersQueryState;
    canSwitchEntity: boolean;
    reportLabel: string;
    children: ReactNode;
}

const FailedToLoadState = ({
    label,
    query,
}: {
    label: string;
    query: RetryableQueryState;
}) => (
    <EmptyState
        header={`Failed to load ${label}`}
        ctaText="Retry"
        action={() => query.refetch()}
        emphasizeHeader={false}
    />
);

export const TemplateReportQueryState = <TData,>({
    queryResult,
    ledgerId,
    ledgersQuery,
    canSwitchEntity,
    reportLabel,
    children,
}: Props<TData>) => {
    if (ledgersQuery.isLoading) {
        return <Loader />;
    }
    if (ledgersQuery.isError) {
        return <FailedToLoadState label="ledgers" query={ledgersQuery} />;
    }
    if (!ledgerId) {
        return (
            <EmptyState
                header="Nothing to preview"
                body={
                    canSwitchEntity
                        ? "This entity has no Cash ledger. Preview another client or entity."
                        : "This entity has no Cash ledger."
                }
                emphasizeHeader={false}
            />
        );
    }
    if (queryResult.isError) {
        return <FailedToLoadState label={reportLabel} query={queryResult} />;
    }
    return (
        <LoaderOnInitialLoading queryResult={queryResult}>
            {children}
        </LoaderOnInitialLoading>
    );
};
